import { useEffect, useState, useCallback } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Tag, AlertTriangle, ListPlus } from "lucide-react";

const SIZES = ["3-4 years", "5-6 years", "7-8 years", "9-11 years", "12-13 years"];
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const fmtTime = (t) => {
  if (!t || !t.includes(":")) return t || "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "pm" : "am";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")}${ap}`;
};
const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

export default function BookOnline() {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [quote, setQuote] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    child_first: "", child_last: "", dob_day: "", dob_month: "", dob_year: "",
    tshirt: "", adult_name: "", adult_email: "", emergency: "", medical_info: "",
    addr_line1: "", addr_line2: "", town: "", postcode: "",
    password: "", password2: "", attends: false, risk: false, marketing: false,
    photo: "yes", signature: "",
  });

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const { user } = useAuth();
  const loggedInParent = user && user.role === "parent";
  const selected = classes.find((c) => c.id === classId);
  const isFull = selected && (
    selected.spaces_remaining <= 0 ||
    (selected.waiting_count > 0 && !(quote?.can_book))
  );

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loggedInParent) setF((s) => ({ ...s, adult_name: user.name || "", adult_email: user.email || "" }));
  }, [loggedInParent, user]);

  const fetchQuote = useCallback(async () => {
    if (!classId) return;
    try {
      const { data } = await api.post(`/bookings/quote?class_id=${classId}&email=${encodeURIComponent(f.adult_email)}&coupon_code=${encodeURIComponent(coupon)}`);
      setQuote(data);
    } catch { /* ignore */ }
  }, [classId, f.adult_email, coupon]);
  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const fullAddress = () => [f.addr_line1, f.addr_line2, f.town, f.postcode].filter(Boolean).join(", ");

  const joinWaitlist = async () => {
    if (!f.child_first || !f.child_last || !f.adult_name || !f.adult_email || !f.emergency) {
      toast.error("Please complete the child's name, your name, email and contact number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/waiting-list", {
        class_id: classId,
        child_first_name: f.child_first, child_last_name: f.child_last,
        adult_name: f.adult_name, adult_email: f.adult_email, phone: f.emergency, notes: "",
      });
      toast.success("You've been added to the waiting list! We'll be in touch when a space opens up.");
      setF((s) => ({ ...s, child_first: "", child_last: "" }));
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isFull) { joinWaitlist(); return; }

    // Validate all required fields explicitly (HTML required is bypassed by e.preventDefault)
    if (!classId) { toast.error("Please select a class."); return; }
    if (!f.child_first.trim() || !f.child_last.trim()) { toast.error("Please enter the child's full name."); return; }
    const day = parseInt(f.dob_day), month = parseInt(f.dob_month), year = parseInt(f.dob_year);
    if (!f.dob_day || isNaN(day) || day < 1 || day > 31) { toast.error("Please enter a valid day of birth."); return; }
    if (!f.dob_month || isNaN(month)) { toast.error("Please select a birth month."); return; }
    if (!f.dob_year || isNaN(year)) { toast.error("Please select a birth year."); return; }
    if (!f.tshirt) { toast.error("Please select a t-shirt size."); return; }
    if (!f.adult_name.trim()) { toast.error("Please enter your full name."); return; }
    if (!f.adult_email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.adult_email)) { toast.error("Please enter a valid email address."); return; }
    if (!f.emergency.trim()) { toast.error("Please enter an emergency contact number."); return; }
    if (!f.addr_line1.trim() || !f.town.trim() || !f.postcode.trim()) { toast.error("Please complete your address details."); return; }
    if (!loggedInParent && !f.password) { toast.error("Please create a password for your Member Portal."); return; }
    if (!loggedInParent && f.password !== f.password2) { toast.error("Passwords do not match. Please re-enter."); return; }
    if (!f.attends || !f.risk) { toast.error("Please confirm the required declarations."); return; }
    if (!f.signature) { toast.error("Please provide your signature."); return; }

    setLoading(true);
    try {
      const payload = {
        class_id: classId,
        child: {
          first_name: f.child_first.trim(), last_name: f.child_last.trim(),
          dob_day: day, dob_month: month, dob_year: year,
          tshirt_size: f.tshirt,
          medical_info: f.medical_info || "",
        },
        adult_name: f.adult_name.trim(), adult_email: f.adult_email.trim().toLowerCase(),
        emergency_contact: f.emergency.trim(),
        address: fullAddress(), password: loggedInParent ? undefined : f.password,
        declarations: {
          attends_school: f.attends, risk_acknowledged: f.risk,
          marketing_consent: f.marketing, photography_consent: f.photo === "yes",
          signature: f.signature,
        },
        coupon_code: coupon || null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/bookings/checkout", payload);
      if (!data.url) throw new Error("No checkout URL returned.");
      window.location.href = data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail ? (typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(" ") : "Something went wrong.") : (err.message || "Something went wrong. Please try again."));
      setLoading(false);
    }
  };

  const years = Array.from({ length: 16 }, (_, i) => new Date().getFullYear() - i);

  return (
    <PublicLayout>
      <div className="max-w-5xl mx-auto px-4 py-10">
        <h1 className="font-display text-5xl text-primary text-center uppercase">Book Online</h1>
        <p className="mt-4 text-center text-lg text-slate-600 max-w-2xl mx-auto">
          Choose your class, complete the registration form and pay securely. Your account is created automatically
          once payment is complete.
        </p>

        <form onSubmit={submit} className="mt-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card title="1. Choose Your Class">
              <Select value={classId} onValueChange={setClassId} required>
                <SelectTrigger data-testid="book-class-select" className="rounded-xl h-auto min-h-11 py-2 text-left">
                  <SelectValue placeholder="Select a class" />
                </SelectTrigger>
                <SelectContent className="max-w-[92vw] sm:max-w-md">
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id} className="whitespace-normal break-words py-2">
                      {c.day_name} – {c.school}
                      <span className="block text-xs opacity-70">
                        {fmtTime(c.start_time)}-{fmtTime(c.end_time)}{(c.spaces_remaining <= 0 || c.waiting_count > 0) ? " · Full — join waiting list" : ""}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isFull && (
                <div data-testid="book-full-notice" className="mt-4 rounded-2xl bg-destructive/10 border border-destructive/30 p-4 flex gap-3">
                  <AlertTriangle className="text-destructive shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-700">
                    <b className="text-destructive">Sorry, this class is currently full.</b> No spaces are available right now.
                    {" "}You can <Link to="/waiting-list" className="underline font-semibold text-primary hover:text-primary/80">join the waiting list</Link> and we'll contact you as soon as a space becomes available — no payment required.
                  </div>
                </div>
              )}
            </Card>

            <Card title="2. Child's Details">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Child's First Name"><Input data-testid="book-child-first" className="rounded-xl" value={f.child_first} onChange={set("child_first")} required /></Field>
                <Field label="Child's Last Name"><Input data-testid="book-child-last" className="rounded-xl" value={f.child_last} onChange={set("child_last")} required /></Field>
              </div>
              {!isFull && (
                <>
                  <Label className="mt-4 block">Child's Date of Birth</Label>
                  <div className="grid grid-cols-3 gap-3 mt-1">
                    <Input data-testid="book-dob-day" type="number" min="1" max="31" placeholder="Day" className="rounded-xl" value={f.dob_day} onChange={set("dob_day")} required={!isFull} />
                    <Select value={f.dob_month} onValueChange={set("dob_month")} required={!isFull}>
                      <SelectTrigger data-testid="book-dob-month" className="rounded-xl"><SelectValue placeholder="Month" /></SelectTrigger>
                      <SelectContent className="max-w-[92vw]">{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={f.dob_year} onValueChange={set("dob_year")} required={!isFull}>
                      <SelectTrigger data-testid="book-dob-year" className="rounded-xl"><SelectValue placeholder="Year" /></SelectTrigger>
                      <SelectContent className="max-w-[92vw]">{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Field label="T-shirt Size" className="mt-4">
                    <Select value={f.tshirt} onValueChange={set("tshirt")} required={!isFull}>
                      <SelectTrigger data-testid="book-tshirt" className="rounded-xl"><SelectValue placeholder="Select size" /></SelectTrigger>
                      <SelectContent className="max-w-[92vw]">{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </>
              )}
            </Card>

            {!isFull && (
              <Card title="3. Medical Information">
                <p className="text-sm text-slate-500 mb-4">
                  Please share any medical conditions, allergies, or additional needs our instructors should be aware of.
                  This information is kept private and only visible to your child's coach.
                </p>
                <Field label="Medical conditions, allergies &amp; additional needs (optional)">
                  <Textarea
                    data-testid="book-medical-info"
                    className="rounded-xl"
                    rows={4}
                    placeholder="E.g. asthma inhaler needed, nut allergy, ADHD, hearing impairment, any specific support requirements..."
                    value={f.medical_info}
                    onChange={set("medical_info")}
                  />
                </Field>
              </Card>
            )}

            <Card title={isFull ? "3. Parent / Guardian Details" : "4. Parent / Guardian Details"}>
              <div className="space-y-4">
                {loggedInParent && (
                  <div className="rounded-2xl bg-primary/5 border border-primary/15 p-3 text-sm text-slate-700">
                    Booking as <b>{user.name}</b> — this child will be added to your existing account (no starter fee, no need to re-enter your details).
                  </div>
                )}
                <Field label="Adult Full Name"><Input data-testid="book-adult-name" className="rounded-xl" value={f.adult_name} onChange={set("adult_name")} disabled={loggedInParent} required /></Field>
                <Field label="Adult Email Address"><Input data-testid="book-adult-email" type="email" className="rounded-xl" value={f.adult_email} onChange={set("adult_email")} onBlur={fetchQuote} disabled={loggedInParent} required /></Field>
                <Field label="Emergency Contact Number"><Input data-testid="book-emergency" className="rounded-xl" value={f.emergency} onChange={set("emergency")} required /></Field>

                {!isFull && (
                  <>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label="Postcode"><Input data-testid="book-postcode" className="rounded-xl uppercase" value={f.postcode} onChange={set("postcode")} required={!isFull} /></Field>
                      <Field label="Town / City"><Input data-testid="book-town" className="rounded-xl" value={f.town} onChange={set("town")} required={!isFull} /></Field>
                    </div>
                    <Field label="Address Line 1 (house/building & street)"><Input data-testid="book-addr1" className="rounded-xl" value={f.addr_line1} onChange={set("addr_line1")} required={!isFull} /></Field>
                    <Field label="Address Line 2 (optional)"><Input data-testid="book-addr2" className="rounded-xl" value={f.addr_line2} onChange={set("addr_line2")} /></Field>

                    {!loggedInParent && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Create a Password (for your Member Portal)"><Input data-testid="book-password" type="password" minLength={6} className="rounded-xl" value={f.password} onChange={set("password")} required={!isFull} /></Field>
                        <Field label="Confirm Password">
                          <Input data-testid="book-password2" type="password" minLength={6} className="rounded-xl" value={f.password2} onChange={set("password2")} required={!isFull} />
                          {f.password2 && f.password !== f.password2 && <span className="text-destructive text-xs">Passwords don't match</span>}
                        </Field>
                      </div>
                    )}

                  </>
                )}
              </div>
            </Card>

            {!isFull && (
              <Card title="5. Declarations">
                <div className="space-y-4">
                  <CheckRow id="attends" checked={f.attends} onChange={set("attends")} testid="book-attends"
                    label="I confirm my child attends the selected school." />
                  <CheckRow id="risk" checked={f.risk} onChange={set("risk")} testid="book-risk"
                    label="I acknowledge the risks involved with gymnastics activity." />
                  <CheckRow id="marketing" checked={f.marketing} onChange={set("marketing")} testid="book-marketing"
                    label="I would like to receive occasional marketing updates (optional)." />
                  <div>
                    <Label>Photography Consent</Label>
                    <RadioGroup value={f.photo} onValueChange={set("photo")} className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="yes" data-testid="book-photo-yes" /> Yes</label>
                      <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="no" data-testid="book-photo-no" /> No</label>
                    </RadioGroup>
                  </div>
                  <div>
                    <Label className="mb-2 block">Parent / Guardian Signature</Label>
                    <SignaturePad testid="book-signature" onChange={(v) => setF((s) => ({ ...s, signature: v }))} />
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* SUMMARY */}
          <div className="lg:col-span-1">
            <div className="rounded-3xl bg-white border border-border p-6 sticky top-40 shadow-xl shadow-primary/10">
              <h3 className="font-display text-2xl text-primary">{isFull ? "Waiting List" : "Payment Summary"}</h3>
              {!selected ? (
                <p className="mt-4 text-slate-500 text-sm">Select a class to continue.</p>
              ) : isFull ? (
                <div className="mt-4 space-y-4 text-sm">
                  <div className="rounded-2xl bg-background p-3">
                    <div className="font-semibold text-slate-800">{selected.day_name} – {selected.school}</div>
                    <div className="text-slate-500">{fmtTime(selected.start_time)} - {fmtTime(selected.end_time)}</div>
                  </div>
                  <p className="text-slate-600">This class is full. Add your details and we'll place you on the waiting list — no payment required now.</p>
                  <Button type="submit" disabled={loading} data-testid="book-join-waitlist"
                    className="w-full rounded-full font-display text-lg bg-secondary hover:bg-secondary/90 py-6">
                    {loading ? <Loader2 className="animate-spin" /> : <><ListPlus size={18} className="mr-1" /> Join Waiting List</>}
                  </Button>
                </div>
              ) : (
                <div className="mt-4 space-y-3 text-sm">
                  <div className="rounded-2xl bg-background p-3">
                    <div className="font-semibold text-slate-800">{selected.day_name} – {selected.school}</div>
                    <div className="text-slate-500">{fmtTime(selected.start_time)} - {fmtTime(selected.end_time)}</div>
                  </div>

                  <div className="font-semibold text-slate-700">Due today</div>
                  {quote?.prorata_amount > 0 && (
                    <div>
                      <div className="flex justify-between">
                        <span>Pro-rata ({quote.prorata_sessions} session{quote.prorata_sessions !== 1 ? "s" : ""} × £{quote.per_session_fee?.toFixed(2)})</span>
                        <span className="font-semibold" data-testid="book-prorata">£{quote?.prorata_amount?.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        From {fmtDate(quote.start_date)} to your first billing on {fmtDate(quote.first_billing_date)}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span>Starter Fee</span>
                    {quote?.starter_waived
                      ? <span className="text-slate-400 line-through">£{quote?.starter_fee?.toFixed(2)}</span>
                      : <span className="font-semibold">£{(quote?.starter_fee ?? 30).toFixed(2)}</span>}
                  </div>
                  {quote?.starter_waived && <div className="text-xs text-secondary">Starter fee waived — {quote.waiver_reason}</div>}
                  {quote?.is_reserved && (
                    <div className="text-xs text-secondary font-semibold">Reserved space confirmed for your email.</div>
                  )}

                  <div className="pt-2">
                    <Label className="text-xs flex items-center gap-1"><Tag size={12} /> Discount/Waiver code</Label>
                    <div className="flex gap-2 mt-1">
                      <Input data-testid="book-coupon" className="rounded-xl h-9" value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Optional" />
                      <Button type="button" variant="outline" className="rounded-xl h-9" onClick={fetchQuote} data-testid="book-apply-coupon">Apply</Button>
                    </div>
                  </div>

                  <div className="border-t border-border pt-3 flex justify-between text-lg">
                    <span className="font-display">Total today</span>
                    <span className="font-display text-primary" data-testid="book-total">£{(quote?.total_today ?? selected.monthly_cost).toFixed(2)}</span>
                  </div>

                  <div className="rounded-2xl bg-primary/5 border border-primary/15 p-3 text-xs text-slate-600">
                    <div className="font-semibold text-primary text-sm">Then £{(quote?.monthly ?? selected.monthly_cost).toFixed(2)}/month</div>
                    on the 23rd of each month, September to July (no payment in August).
                    {quote?.first_billing_date && <> First monthly payment: {fmtDate(quote.first_billing_date)}.</>}
                    <br />Your card is saved securely and charged automatically — set it up once and you're done.
                  </div>

                  <Button type="submit" disabled={loading} data-testid="book-pay-btn"
                    className="w-full rounded-full font-display text-lg bg-primary hover:bg-primary/90 py-6 mt-2">
                    {loading ? <Loader2 className="animate-spin" /> : "Pay & Set Up Payments"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </PublicLayout>
  );
}

const Card = ({ title, children }) => (
  <div className="rounded-3xl bg-white border border-border p-6 shadow-sm">
    <h3 className="font-display text-2xl text-slate-800 mb-4">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="mb-1 block">{label}</Label>
    {children}
  </div>
);

const CheckRow = ({ id, checked, onChange, label, testid }) => (
  <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
    <Checkbox id={id} checked={checked} onCheckedChange={onChange} data-testid={testid} className="mt-1" />
    <span className="text-slate-700 text-sm">{label}</span>
  </label>
);
